import CONSTANTS from "src/utils/constants";
import {CalculationSourceType} from "src/models/common/CalculationSource";
import StringUtils from "src/utils/stringUtils";
import { FILTERING_OPERATORS_FOR_STRING, FILTERING_OPERATORS_FOR_NUMBER } from "./Constants";

interface FilterProperties {
    groupValuesLabel: string;
    operators: string[];
}

interface ColumnFilteringProperty {
    propertyLabel: string;
    key: string;
    groupValuesLabel: string;
    operators: string[];
}

interface ColumnDefinition {
    id: string;
    header: string;
    sortingField: string;
    width: number;
    cell: (item: Record<string, any>) => string;
}

export const shouldSkipField = (field: { ACCESSOR: string }, calculationSource: CalculationSourceType, hasJeTagData: boolean): boolean => {
    return (
        (field.ACCESSOR === CONSTANTS.JE_FIELDS.AGREEMENT_LINE_NUM.ACCESSOR && calculationSource === CalculationSourceType.ICRS) ||
        (field.ACCESSOR === CONSTANTS.JE_FIELDS.ICRS_REGISTRATION_NUM.ACCESSOR && calculationSource === CalculationSourceType.APTTUS) ||
        ((field.ACCESSOR === CONSTANTS.JE_FIELDS.JE_TAG_ID.ACCESSOR || field.ACCESSOR === CONSTANTS.JE_FIELDS.JE_TAG_DESCRIPTION.ACCESSOR) && !hasJeTagData)
    );
};

const getCustomPropertiesForColumnFiltering = (column: string): FilterProperties => {
    return {
        groupValuesLabel: column,
        operators: isNumericColumn(column) ? FILTERING_OPERATORS_FOR_NUMBER : FILTERING_OPERATORS_FOR_STRING
    };
};

const isNumericColumn = (column: string): boolean => {
    return [
        CONSTANTS.JE_FIELDS.ENTERED_DR.ACCESSOR,
        CONSTANTS.JE_FIELDS.ENTERED_CR.ACCESSOR,
        CONSTANTS.JE_FIELDS.ACCOUNTED_DR.ACCESSOR,
        CONSTANTS.JE_FIELDS.ACCOUNTED_CR.ACCESSOR,
        CONSTANTS.JE_FIELDS.EXCHANGE_RATE.ACCESSOR
    ].includes(column);
};

export const getJournalEntriesFilteringProperties = (calculationSource: CalculationSourceType, hasJeTagData: boolean): ColumnFilteringProperty[] => {
    const columnFilteringProperties: ColumnFilteringProperty[] = [];

    Object.values(CONSTANTS.JE_FIELDS).forEach((field) => {
        if (!shouldSkipField(field, calculationSource, hasJeTagData)) {
            columnFilteringProperties.push({
                propertyLabel: field.DISPLAY,
                key: field.ACCESSOR,
                ...getCustomPropertiesForColumnFiltering(field.ACCESSOR)
            });
        }
    });

    return columnFilteringProperties;
};

export const getColumnDefinitions = (calculationSource: CalculationSourceType, hasJeTagData: boolean): ColumnDefinition[] => {
    const colDefs: ColumnDefinition[] = [];
    Object.values(CONSTANTS.JE_FIELDS).forEach((field) => {
        if (!shouldSkipField(field, calculationSource, hasJeTagData)) {
            colDefs.push({
                id: field.ACCESSOR,
                header: field.DISPLAY,
                sortingField: field.ACCESSOR,
                width: 200,
                cell: (item: Record<string, any>) => item[field.ACCESSOR] ?? ""
            });
        }
    });
    return colDefs;
};

export const getJEFieldsTableColumns = (hasJeTagData: boolean) => {
    return Object.values(CONSTANTS.JE_FIELDS)
        .filter(field => {
            if ((field.ACCESSOR === CONSTANTS.JE_FIELDS.JE_TAG_ID.ACCESSOR ||
                field.ACCESSOR === CONSTANTS.JE_FIELDS.JE_TAG_DESCRIPTION.ACCESSOR) && !hasJeTagData) {
                return false;
            }
            return true;
        })
        .map(field => field.ACCESSOR);
};

export const getVisibleContentOptions = (hasJeTagData: boolean) => {
    return getJEFieldsTableColumns(hasJeTagData).map(column => ({
        id: column,
        label: StringUtils.convertCamelCaseToTitleCase(column)
    }));
};

export const getDefaultCollectionPreferences = (hasJeTagData: boolean) => {
    return {
        pageSize: 100,
        visibleContent: getJEFieldsTableColumns(hasJeTagData),
        wrapLines: false,
        stickyColumns: {first: 0, last: 0}
    };
};
